import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Download, X, CheckCircle, AlertTriangle } from 'lucide-react';

const DATE_RANGES = [
  { label: '1h', hours: 1 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
  { label: '30d', hours: 720 },
];

const DeveloperGeminiLogs = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(24);
  const [fnFilter, setFnFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    if (!organizationId) return;
    loadLogs();
  }, [organizationId, range, fnFilter]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const since = new Date(Date.now() - range * 3600000).toISOString();
      let q = supabase
        .from('gemini_usage_log')
        .select('*')
        .eq('organization_id', organizationId)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(500);

      if (fnFilter !== 'all') q = q.eq('function_name', fnFilter);
      const { data } = await q;
      setLogs(data || []);
    } catch (err) {
      console.error('Error loading gemini logs:', err);
    }
    setLoading(false);
  };

  const totalCalls = logs.length;
  const successCount = logs.filter(l => l.success).length;
  const successRate = totalCalls > 0 ? Math.round((successCount / totalCalls) * 100) : 0;
  const totalInput = logs.reduce((s, l) => s + (l.input_token_estimate || 0), 0);
  const totalOutput = logs.reduce((s, l) => s + (l.output_token_estimate || 0), 0);
  const avgDuration = totalCalls > 0 ? Math.round(logs.reduce((s, l) => s + (l.duration_ms || 0), 0) / totalCalls / 1000 * 10) / 10 : 0;
  const estCost = (totalInput * 0.075 + totalOutput * 0.30) / 1_000_000;

  const fnNames = [...new Set(logs.map(l => l.function_name).filter(Boolean))];

  const exportCSV = () => {
    const headers = ['time', 'function', 'model', 'input_tokens', 'output_tokens', 'duration_ms', 'success', 'error'];
    const rows = logs.map(l => [
      l.created_at, l.function_name, l.model, l.input_token_estimate, l.output_token_estimate, l.duration_ms, l.success, l.error_message || ''
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'gemini-logs.csv'; a.click();
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/developer')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
        <ArrowLeft className="h-4 w-4" /> Back to Developer
      </button>

      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
          Gemini Usage Logs
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV} className={`px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1.5 ${isDark ? 'text-[#a1a1aa] hover:bg-[#18181b]' : 'text-[#666] hover:bg-[#f0f0f0]'}`}>
            <Download className="h-3.5 w-3.5" /> Export
          </button>
          <button onClick={loadLogs} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-[#18181b] text-[#a1a1aa]' : 'hover:bg-[#f0f0f0] text-[#666]'}`}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          {DATE_RANGES.map(r => (
            <button
              key={r.label}
              onClick={() => setRange(r.hours)}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                range === r.hours
                  ? isDark ? 'bg-[#18181b] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                  : isDark ? 'text-[#52525b] hover:text-[#a1a1aa]' : 'text-[#999] hover:text-[#666]'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <select
          value={fnFilter}
          onChange={e => setFnFilter(e.target.value)}
          className={`text-xs px-3 py-1.5 rounded-lg border ${isDark ? 'bg-[#18181b] border-[#27272a] text-white' : 'bg-white border-[#e5e5e5] text-[#1a1a1a]'}`}
        >
          <option value="all">All functions</option>
          {fnNames.map(fn => <option key={fn} value={fn}>{fn}</option>)}
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {[
          { label: 'Total Calls', value: totalCalls },
          { label: 'Success Rate', value: `${successRate}%` },
          { label: 'Input Tokens', value: `${(totalInput / 1000).toFixed(0)}K` },
          { label: 'Output Tokens', value: `${(totalOutput / 1000).toFixed(0)}K` },
          { label: 'Avg Duration', value: `${avgDuration}s` },
          { label: 'Est. Cost', value: `$${estCost.toFixed(4)}` },
        ].map(s => (
          <div key={s.label} className={`${cardClass} p-3`}>
            <span className={`text-[10px] block ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{s.label}</span>
            <span className={`text-lg font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.value}</span>
          </div>
        ))}
      </div>

      {/* Logs Table */}
      <div className={cardClass}>
        <div className={`grid grid-cols-7 gap-3 px-5 py-2.5 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
          <span>Time</span>
          <span>Function</span>
          <span>Model</span>
          <span>Input</span>
          <span>Output</span>
          <span>Duration</span>
          <span>Status</span>
        </div>
        <div className={`max-h-[500px] overflow-y-auto divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
          {loading ? (
            <div className={`px-5 py-12 text-center text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Loading...</div>
          ) : logs.length === 0 ? (
            <div className={`px-5 py-12 text-center text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>No logs found</div>
          ) : logs.map(l => (
            <button
              key={l.id}
              onClick={() => setSelectedLog(l)}
              className={`w-full grid grid-cols-7 gap-3 px-5 py-3 items-center text-left transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
            >
              <span className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                {new Date(l.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded w-fit ${isDark ? 'bg-[#18181b] text-[#a1a1aa]' : 'bg-[#f5f5f5] text-[#666]'}`}>
                {(l.function_name || '').replace(/-/g, ' ')}
              </span>
              <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{l.model || '--'}</span>
              <span className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{l.input_token_estimate || '--'}</span>
              <span className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{l.output_token_estimate || '--'}</span>
              <span className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{l.duration_ms ? `${(l.duration_ms / 1000).toFixed(1)}s` : '--'}</span>
              <span>
                {l.success
                  ? <span className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle className="h-3 w-3" />OK</span>
                  : <span className="flex items-center gap-1 text-xs text-red-400"><AlertTriangle className="h-3 w-3" />Error</span>
                }
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedLog(null)}>
          <div className={`${cardClass} w-full max-w-lg max-h-[80vh] overflow-y-auto m-4 p-5`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Log Detail</h3>
              <button onClick={() => setSelectedLog(null)} className={`p-1 rounded ${isDark ? 'hover:bg-[#18181b]' : 'hover:bg-[#f0f0f0]'}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className={`space-y-3 text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
              <div><span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>Time:</span> {new Date(selectedLog.created_at).toLocaleString()}</div>
              <div><span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>Function:</span> {selectedLog.function_name}</div>
              <div><span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>Model:</span> {selectedLog.model}</div>
              <div><span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>Duration:</span> {selectedLog.duration_ms}ms</div>
              <div><span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>Input Tokens:</span> {selectedLog.input_token_estimate} ({selectedLog.input_text_length} chars)</div>
              <div><span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>Output Tokens:</span> {selectedLog.output_token_estimate} ({selectedLog.output_text_length} chars)</div>
              {selectedLog.session_id && <div><span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>Session:</span> <code className={`text-xs ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>{selectedLog.session_id}</code></div>}
              {selectedLog.error_message && <div className="text-red-400"><span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>Error:</span> {selectedLog.error_message}</div>}
              {selectedLog.prompt_summary && (
                <div>
                  <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Prompt Summary</span>
                  <pre className={`mt-1 p-2 rounded text-xs max-h-32 overflow-y-auto ${isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'}`}>{selectedLog.prompt_summary.slice(0, 500)}</pre>
                </div>
              )}
              {selectedLog.response_summary && (
                <div>
                  <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Response Summary</span>
                  <pre className={`mt-1 p-2 rounded text-xs max-h-32 overflow-y-auto ${isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'}`}>{selectedLog.response_summary.slice(0, 500)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeveloperGeminiLogs;
